import { Table } from 'antd';
import { type TableRowSelection } from 'antd/lib/table/interface';
import React, { type Key, useEffect } from 'react';
import EmptyTableOverlay from '../../components/Common/EmptyTableOverlay';
import type File from '../../interfaces/File';
import type TableEmptyState from '../../interfaces/TableEmptyState';
interface FilesTableProps {
  files: File[];
  isLoading: boolean;
  handleLoadMore: () => void;
  children: React.ReactNode;
  rowSelection: TableRowSelection<File>;
  handleTableChange: (pagination: any, tableFilters: any, sorter: any) => void;
  tableScrollY: number;
  handleFileRowClick: (file: File, hasClickedOnFilename: boolean, event?: React.MouseEvent) => void;
  handleFileRowDoubleClick: (file: File) => void;
  handleRowContextMenu: (record: File, event: React.MouseEvent) => void;
  setSelectedTableKeys: (keys: Key[]) => void;
  selectedTableKeys: Key[];
  emptyState: TableEmptyState;
}

const FilesTable: React.FC<FilesTableProps> = ({
  files,
  isLoading,
  handleLoadMore,
  children,
  rowSelection,
  handleTableChange,
  tableScrollY,
  handleFileRowClick,
  handleFileRowDoubleClick,
  handleRowContextMenu,
  setSelectedTableKeys,
  selectedTableKeys,
  emptyState,
}) => {
  useEffect(() => {
    const body = document.querySelector<HTMLElement>('.ant-table-body');
    if (!body) return;

    let hideTimeout: number | null = null;

    const show = () => {
      body.classList.remove('scroll-hidden');
      body.classList.add('scroll-visible');
      if (hideTimeout) {
        clearTimeout(hideTimeout);
        hideTimeout = null;
      }
    };
    const hide = () => {
      hideTimeout = window.setTimeout(() => {
        body.classList.remove('scroll-visible');
        body.classList.add('scroll-hidden');
      }, 100);
    };

    body.classList.add('scroll-hidden');
    body.addEventListener('mouseenter', show);
    body.addEventListener('scroll', show, { passive: true });
    body.addEventListener('mouseleave', hide);

    return () => {
      body.removeEventListener('mouseenter', show);
      body.removeEventListener('scroll', show);
      body.removeEventListener('mouseleave', hide);
      if (hideTimeout) clearTimeout(hideTimeout);
    };
  }, [tableScrollY]);

  return (
    <div className="file-table relative flex h-full w-full flex-col">
      <Table<File>
        rowKey="id"
        rowSelection={rowSelection}
        loading={isLoading}
        pagination={false}
        onChange={handleTableChange}
        onScroll={(e) => {
          const scrollableElement = e.target as HTMLElement;
          if (
            scrollableElement.scrollTop + scrollableElement.clientHeight >=
            scrollableElement.scrollHeight - 100
          ) {
            handleLoadMore();
          }
        }}
        dataSource={files}
        showSorterTooltip={false}
        scroll={{ x: 'max-content', y: tableScrollY }}
        rowClassName={() => 'my-table-row'}
        className="file-table select-none"
        onRow={(record: File) => ({
          onClick: (event) => {
            if (
              event.target instanceof HTMLElement &&
              event.target.classList.contains('ant-table-selection-column')
            ) {
              setSelectedTableKeys(
                selectedTableKeys.includes(record.id)
                  ? selectedTableKeys.filter((key) => key !== record.id)
                  : [...selectedTableKeys, record.id],
              );
              return;
            }

            // Delegate also to your existing handler
            const clickedOnFilename = Boolean(
              (event.target as HTMLElement).closest('.filename-click-handler'),
            );
            handleFileRowClick(record, clickedOnFilename, event);
          },
          onDoubleClick: (event) => {
            event.preventDefault();
            event.stopPropagation();
            handleFileRowDoubleClick(record);
          },

          onContextMenu: (event) => handleRowContextMenu(record, event),
        })}
      >
        {children}
      </Table>

      <EmptyTableOverlay
        icon={emptyState.iconName}
        label={emptyState.label}
        isVisible={files.length === 0}
        isLoading={isLoading}
      />
    </div>
  );
};

export default FilesTable;
